import type { ComponentConfig } from "replo-runtime/store/components";
import type { CustomPropsRecord } from "schemas/component";

import { convertToLegacyProps } from "replo-runtime/shared/utils/props";

function getConfigurableProps(): CustomPropsRecord {
  return {
    bundleTargetType: {
      id: "bundleTargetType",
      type: "selectableButtons",
      name: "Bundle is made of",
      defaultValue: "products",
      selectableValues: {
        type: "options",
        options: [
          { label: "Products", value: "products" },
          { label: "Variants", value: "variants" },
        ],
      },
    },
    productsWithVariants: {
      id: "bundleTargets",
      type: "productsWithVariants",
      name: "Products",
      defaultValue: null,
    },
    bundleDiscountThresholds: {
      id: "bundleDiscountThresholds",
      type: "bundleDiscountThresholds",
      name: "Configure Bundle",
      defaultValue: [
        { quantity: 3, discount: { type: "percentageOff", value: 25 } },
      ],
    },
    allowMultipleOfSameItem: {
      id: "allowMultipleOfSameItem",
      type: "boolean",
      name: "Allow multiple of same item",
      description: "Users can add more than one of each product/variant",
      defaultValue: true,
    },
  };
}

const defaultButtonStyles = {
  root: {
    defaultStyles: {
      color: "#FFFFFFFF",
      borderTopColor: "#000000",
      borderLeftColor: "#000000",
      borderBottomColor: "#000000",
      borderRightColor: "#000000",
      width: "100%",
      border: "none",
      padding: "12px 16px",
      fontSize: "16px",
      textAlign: "center",
      fontWeight: "600",
      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",
      borderBottomLeftRadius: "6px",
      borderBottomRightRadius: "6px",
      backgroundColor: "#000000FF",
    },
  },
} as const;

const config = {
  renderData: {
    customProps: convertToLegacyProps(getConfigurableProps()),
    newInstancesUseNumbering: true,
    allowsLayoutModification: false,
    acceptsArbitraryChildren: () => false,
    isAlwaysDynamic: false,
    canvasIndicatorDragDirections: [],
    canContainChildren: false,
    showComponentControlsFromChildren: true,
    allowChildrenReorder: false,
  },
  children: {
    bundleAddToBundleButton: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleProductTile"],
          message:
            "Add to Bundle Button can only be used within a Bundle Product Tile",
        },
        styleElements: defaultButtonStyles,
        allowChildrenReorder: false,
      },
    },
    bundleProductVariantSelector: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleProductTile"],
          message:
            "Bundle Product Variant Selector can only be used within a Bundle Product Tile",
        },
        styleElements: {
          root: {
            defaultStyles: {
              width: "100%",
              paddingTop: "2px",
              paddingBottom: "2px",
              paddingLeft: "8px",
              paddingRight: "8px",
            },
          },
        },
        allowChildrenReorder: false,
      },
    },
    bundleAddToCartButton: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleSummary"],
          message:
            "Add to Cart Button can only be used within a Bundle Summary",
        },
        styleElements: defaultButtonStyles,
        allowChildrenReorder: false,
      },
    },
    bundleCart: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleSummary"],
          message: "Bundle Cart can only be used within a Bundle Summary",
        },
        styleElements: { root: {} },
        allowChildrenReorder: false,
      },
    },
    bundleCartTotals: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleSummary"],
          message:
            "Bundle Cart Totals can only be used within a Bundle Summary",
        },
        styleElements: { root: {} },
        allowChildrenReorder: false,
      },
    },
    bundleFooter: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleBuilder"],
          message: "Bundle Footer can only be used within a Bundle Builder",
        },
        styleElements: { root: {} },
        allowChildrenReorder: false,
      },
    },
    bundleGrid: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: true,
        allowChildrenReorder: false,
        ancestorAllow: {
          ancestorTypes: ["bundleBuilder"],
          message: "Bundle Grid can only be used within a Bundle Builder",
        },
        styleElements: { root: {} },
      },
    },
    bundleHeader: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => true,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleBuilder"],
          message: "Bundle Header can only be used within a Bundle Builder",
        },
        styleElements: { root: {} },
        allowChildrenReorder: true,
      },
    },
    bundleProgressBar: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleBuilder"],
          message:
            "Bundle Progress Bar can only be used within a Bundle Builder",
        },
        styleElements: { root: {} },
        allowChildrenReorder: false,
      },
    },
    bundleSummary: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => true,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: true,
        allowChildrenReorder: false,
        ancestorAllow: {
          ancestorTypes: ["bundleBuilder"],
          message: "Bundle Summary can only be used within a Bundle Builder",
        },
        styleElements: {
          root: {
            defaultStyles: {
              position: "sticky",
              top: 0,
              left: 0,
            },
          },
        },
      },
    },
    bundleTierSelector: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleSummary", "bundleHeader"],
          message:
            "Bundle Tier Selector can only be used within a Bundle Builder",
        },
        styleElements: { root: {} },
        allowChildrenReorder: false,
      },
    },
    bundleProductTile: {
      renderData: {
        preventDeleteMessage:
          "This component cannot be removed from Bundle Builder.",
        newInstancesUseNumbering: true,
        allowsLayoutModification: false,
        acceptsArbitraryChildren: () => false,
        isAlwaysDynamic: false,
        canvasIndicatorDragDirections: [],
        canContainChildren: false,
        ancestorAllow: {
          ancestorTypes: ["bundleGrid"],
          message: "Product Tile can only be used within a Bundle Grid",
        },
        styleElements: { root: {} },
        allowChildrenReorder: false,
      },
    },
  },
} satisfies ComponentConfig;

export default config;
